// The Interaction: one durable record of every exchange between a hub's
// agent and its curator. It replaces the Curator ask's two kinds, question
// and price, with a purpose, a reply schema and an optional subject, so a
// new pause is a purpose and a subject kind, never a new wire field. Three
// parts build against it: the HubAgent Durable Object, which stores
// Interactions and checks each Curator reply with checkReply; the Exchange,
// which decodes what the object returns; and Curator Studio, which renders
// any reply schema and runs the same checkReply before it sends. It imports
// only effect and the Tool SDK, as the rest of the contract does. See
// Design > Hub agent, "Pausing for the curator".

import { Schema } from "effect";
import type { Json } from "../tool-sdk/json.ts";

// An Interaction's ID, in the hub event ID charset: `interaction:<tool call
// ID>`, or `ask:<tool call ID>` for one converted from a Curator ask, which
// kept its ID so its Curator reply's hub event still names it. At most 120
// characters, so a Curator reply's hub event carries it whole: in `ref`,
// whose values are at most 128 characters, and in its eventId,
// `curator.reply:<id>`, at most 300. A brand, so it can't go where an agent
// action's ID belongs (Design > Code standards, "IDs that cross a service
// are branded").
export const MAX_INTERACTION_ID = 120;
export const InteractionId = Schema.String.check(Schema.isPattern(new RegExp(`^[A-Za-z0-9._:-]{3,${MAX_INTERACTION_ID}}$`))).pipe(Schema.brand("InteractionId")).annotate({ identifier: "InteractionId" });
export type InteractionId = typeof InteractionId.Type;

/**
 * What an Interaction asks of the curator. clarify: supply information or
 * say what they want; select: choose among options the agent offers; review:
 * judge a piece of work, approve it or ask for a revision; authorize: permit
 * one change, on one version, in one scope; inform: nothing, it never blocks.
 */
export const PURPOSES = ["clarify", "select", "review", "authorize", "inform"] as const;
export const Purpose = Schema.Literals(PURPOSES);
export type Purpose = typeof Purpose.Type;

/** An option's key: stable, never a label, so a label can change without changing what a reply means. */
export const OptionKey = Schema.String.check(Schema.isPattern(/^[a-z][A-Za-z0-9_-]{0,39}$/)).annotate({ identifier: "OptionKey" });
export type OptionKey = typeof OptionKey.Type;

const Option = Schema.Struct({
  key: OptionKey,
  label: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
  detail: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(1000))),
  requiresNote: Schema.optionalKey(Schema.Boolean),
});
export type ChoiceOption = typeof Option.Type;

const TextReply = Schema.Struct({ type: Schema.Literal("text"), maxLength: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 4000 })) });
const ChoiceReply = Schema.Struct({
  type: Schema.Literal("choice"),
  options: Schema.Array(Option).check(Schema.isMinLength(1), Schema.isMaxLength(40)),
  min: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  max: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  // Shown to the curator as the agent's suggestion; never applied for them, on expiry or otherwise.
  recommended: Schema.optionalKey(OptionKey),
  // A custom answer, in the curator's words. `exclusive`: given only with no option chosen, as an
  // authorize Interaction's different price is, so a choice can never carry it.
  other: Schema.optionalKey(Schema.Struct({ label: Schema.String.check(Schema.isMaxLength(200)), maxLength: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 2000 })), exclusive: Schema.optionalKey(Schema.Boolean) })),
}).check(Schema.makeFilter((s) => {
  if (s.min > s.max) return "min is at most max";
  if (s.max > s.options.length) return "max is at most the number of options";
  if (new Set(s.options.map((o) => o.key)).size !== s.options.length) return "option keys are distinct";
  if (s.recommended !== undefined && !s.options.some((o) => o.key === s.recommended)) return "recommended is one of the options' keys";
  return true;
}));

/** What a valid Curator reply to an Interaction looks like: free text, or a choice among options. */
export const ReplySchema = Schema.Union([TextReply, ChoiceReply]).annotate({ identifier: "ReplySchema" });
export type ReplySchema = typeof ReplySchema.Type;

/**
 * A price change the hub agent proposes for one intent, with the evidence it
 * read. Asks are US dollars per million answers, as a hub manifest prices an
 * intent; bids are what consumers offered, in the same unit. The evidence is
 * the intent's PriceTooLow events in the window: `declined` is how many, and
 * the bids are their median and highest.
 */
export const PriceSuggestion = Schema.Struct({
  intent: Schema.String,
  currentAskUsd: Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0)),
  proposedAskUsd: Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0)),
  evidence: Schema.Struct({
    windowDays: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 90 })),
    declined: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
    medianBidUsd: Schema.NullOr(Schema.Finite),
    maxBidUsd: Schema.NullOr(Schema.Finite),
  }),
  rationale: Schema.String.check(Schema.isMaxLength(2000)),
}).annotate({ identifier: "PriceSuggestion" });
export type PriceSuggestion = typeof PriceSuggestion.Type;

/** One question of a draft question set, with the outcome it expects, as a golden question names it. */
export const DraftQuestion = Schema.Struct({
  question: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500)),
  expectOutcome: Schema.Literals(["Answer", "NotFound", "MissingParameters", "OutOfScope"]),
}).annotate({ identifier: "DraftQuestion" });
export type DraftQuestion = typeof DraftQuestion.Type;

/**
 * The typed piece of work an Interaction is about, with its version: a price
 * change for one intent, a hub's draft question set, or one of the hub's
 * toolsets, by its commit, which an inform names so Curator Studio links its
 * card (decision hub-agent-commit-to-ready-names). An approval holds for
 * this subject, at this version, only.
 */
export const Subject = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("priceChange"), version: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)), price: PriceSuggestion }),
  Schema.Struct({ kind: Schema.Literal("questionSet"), version: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)), questions: Schema.Array(DraftQuestion).check(Schema.isMinLength(1), Schema.isMaxLength(50)) }),
  Schema.Struct({ kind: Schema.Literal("toolset"), version: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)), commit: Schema.String.check(Schema.isPattern(/^(?:[0-9a-f]{40}|[0-9a-f]{64})$/)) }),
]).annotate({ identifier: "Subject" });
export type Subject = typeof Subject.Type;
export type SubjectKind = Subject["kind"];

/**
 * A Curator reply: the keys the curator chose, their custom answer, and a
 * note. For a text reply schema, `other` is the text. Its shape only: what a
 * given Interaction accepts is checkReply's.
 */
export const CuratorReply = Schema.Struct({
  choice: Schema.Array(OptionKey).check(Schema.isMaxLength(40)),
  other: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(4000))),
  note: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(4000))),
}).annotate({ identifier: "CuratorReply" });
export type CuratorReply = typeof CuratorReply.Type;

/** An Interaction's state: open, or the outcome that closed it. Exactly one outcome wins. */
export const INTERACTION_STATES = ["open", "answered", "expired", "superseded", "cancelled"] as const;
export const InteractionState = Schema.Literals(INTERACTION_STATES);
export type InteractionState = typeof InteractionState.Type;

/**
 * The options a purpose fixes, so the agent never writes them: a review is
 * approve, or revise with a note; an authorize is approve, or decline with a
 * note, or a different value in the curator's words, which authorizes
 * nothing and so is never given with approve.
 */
export const FIXED_OPTIONS = {
  review: { type: "choice", min: 1, max: 1, options: [{ key: "approve", label: "Approve" }, { key: "revise", label: "Request changes", requiresNote: true }] },
  authorize: {
    type: "choice", min: 0, max: 1,
    options: [{ key: "approve", label: "Approve" }, { key: "decline", label: "Decline", requiresNote: true }],
    other: { label: "Something different", maxLength: 500, exclusive: true },
  },
} as const satisfies Record<"review" | "authorize", ReplySchema>;

/** A Curator reply that checkReply took, normalized: keys in the options' order, text trimmed. */
export type CheckedReply = { readonly ok: true; readonly reply: CuratorReply } | { readonly ok: false; readonly detail: string };

const trimmed = (s: string | undefined): string | undefined => {
  const t = s?.trim();
  return t === undefined || t === "" ? undefined : t;
};

/**
 * Checks a Curator reply against its Interaction's reply schema. Pure: Studio
 * runs it before it sends, and the HubAgent before it keeps a reply, so the
 * two can't disagree. Its words are ours, shown to the curator.
 */
export function checkReply(schema: ReplySchema, reply: CuratorReply): CheckedReply {
  const other = trimmed(reply.other);
  const note = trimmed(reply.note);
  const normal = (choice: readonly OptionKey[]): CuratorReply => ({ choice, ...(other === undefined ? {} : { other }), ...(note === undefined ? {} : { note }) });
  switch (schema.type) {
    case "text": {
      if (reply.choice.length > 0) return { ok: false, detail: "This takes a written reply, not a choice." };
      if (other === undefined) return { ok: false, detail: "Write a reply." };
      if (other.length > schema.maxLength) return { ok: false, detail: `A reply is at most ${schema.maxLength} characters.` };
      return { ok: true, reply: normal([]) };
    }
    case "choice": {
      if (new Set(reply.choice).size !== reply.choice.length) return { ok: false, detail: "Choose each option once." };
      const chosen = schema.options.filter((o) => reply.choice.includes(o.key));
      if (chosen.length !== reply.choice.length) return { ok: false, detail: "That isn't one of the options." };
      if (other !== undefined) {
        if (schema.other === undefined) return { ok: false, detail: "This takes one of the options, not your own answer." };
        if (other.length > schema.other.maxLength) return { ok: false, detail: `Your own answer is at most ${schema.other.maxLength} characters.` };
        if (schema.other.exclusive === true && chosen.length > 0) return { ok: false, detail: `Choose an option or give your own answer, not both.` };
      }
      if (chosen.length === 0 && other === undefined && schema.min === 0) return { ok: false, detail: schema.other ? "Choose an option or give your own answer." : "Choose an option." };
      // An exclusive answer of their own stands alone; any other counts as one
      // toward min, so it never weighs more than an option. max counts options only.
      const counted = chosen.length + (other !== undefined && schema.other?.exclusive !== true ? 1 : 0);
      if (other === undefined || schema.other?.exclusive !== true) {
        if (counted < schema.min) return { ok: false, detail: schema.min === 1 ? "Choose an option." : `Choose at least ${schema.min}.` };
        if (chosen.length > schema.max) return { ok: false, detail: schema.max === 1 ? "Choose one option." : `Choose at most ${schema.max}.` };
      }
      const needsNote = chosen.find((o) => o.requiresNote === true);
      if (needsNote !== undefined && note === undefined) return { ok: false, detail: `Say why, in a note, when you choose "${needsNote.label}".` };
      return { ok: true, reply: normal(chosen.map((o) => o.key)) };
    }
    default:
      return schema satisfies never;
  }
}

// JSON with object keys sorted, so two subjects compare by value whatever their key order.
const canonical = (value: Subject): string => JSON.stringify(value, (_key: string, v: Json): Json =>
  v !== null && typeof v === "object" && !Array.isArray(v) ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) : v);

/** What a realization asks before it carries out a change: this change, at this version, in this scope, now. */
export type ApprovalRequest = {
  readonly subject: Subject;          // the change as the realization will carry it out
  readonly scope: string;             // the subject's supersession key, such as priceChange:<intent>
  readonly at: number;                // the realization's clock
};

/** The parts of a stored Interaction approvalHolds reads. */
export type ApprovalRecord = {
  readonly purpose: Purpose;
  readonly state: InteractionState;
  readonly subject: Subject | null;
  readonly subjectKey: string | null;
  readonly reply: CuratorReply | null;
  readonly repliedBy: string | null;
  readonly approvalUntil: number | null;
};

export type ApprovalVerdict =
  | { readonly holds: true; readonly by: string }
  | { readonly holds: false; readonly reason: "notAuthorize" | "notAnswered" | "notApproved" | "otherSubject" | "otherVersion" | "otherPayload" | "expired" | "noApprover" };

/**
 * Whether an authorize Interaction's approval permits a change: an accepted
 * approve, by a curator the record names, of this subject at this version
 * with this exact payload, in this scope, still valid at `at`. Pure, so the
 * action that carries a change out checks it as it runs. It can't see roles:
 * the realization rechecks that `by` still curates the hub. See Design > Hub
 * agent, "Pausing for the curator".
 */
export function approvalHolds(record: ApprovalRecord, request: ApprovalRequest): ApprovalVerdict {
  if (record.purpose !== "authorize") return { holds: false, reason: "notAuthorize" };
  if (record.state !== "answered" || record.reply === null) return { holds: false, reason: "notAnswered" };
  if (record.reply.choice.length !== 1 || record.reply.choice[0] !== "approve" || record.reply.other !== undefined) return { holds: false, reason: "notApproved" };
  if (record.subject === null || record.subject.kind !== request.subject.kind || record.subjectKey !== request.scope) return { holds: false, reason: "otherSubject" };
  if (record.subject.version !== request.subject.version) return { holds: false, reason: "otherVersion" };
  if (canonical(record.subject) !== canonical(request.subject)) return { holds: false, reason: "otherPayload" };
  if (record.approvalUntil === null || record.approvalUntil <= request.at) return { holds: false, reason: "expired" };
  if (record.repliedBy === null) return { holds: false, reason: "noApprover" };
  return { holds: true, by: record.repliedBy };
}
